import { useCallback, useMemo } from 'react';
import { useAccount } from 'wagmi';

import { useCustomer, useI18n, usePopup } from 'common/hooks';

import { memoizedComponent } from 'utils/memoizeComponent';

import Button, { ButtonProps } from '../../../Button/Button.component';
import { Web3ConnectSwitchPopup } from '../../popups/ConnectSwitch/ConnectSwitch.component';
import { Web3ConnectWelcomePopup } from '../../popups/ConnectWelcome/ConnectWelcome.component';
import './index.css';

export const Web3WalletConnectButton = memoizedComponent((props: Partial<ButtonProps>) => {
  const { isConnected } = useAccount();
  const { isAuthenticated } = useCustomer();
  const { show } = usePopup();
  const { translate } = useI18n();

  const title = useMemo(() => {
    if (isConnected) return translate.toText('mobile:wallets:web3:details:button:primary');

    return translate.toText('mobile:wallets:web3:connect:button:primary');
  }, [isConnected, translate]);

  const onWalletSwitch = useCallback(async () => {
    show({
      title: undefined,
      component: <Web3ConnectSwitchPopup />,
      wrapperClassName: 'web3-connect-switch-wrapper',
      hideCloseIcon: false,
      useBottomSheet: true,
    });
  }, [show]);

  const onWalletConnect = useCallback(() => {
    show({
      component: <Web3ConnectWelcomePopup />,
      wrapperClassName: 'web3-connect-welcome-wrapper',
      hideCloseIcon: false,
      useBottomSheet: true,
    });
  }, [show]);

  const onClick = useCallback(() => {
    if (isConnected) return onWalletSwitch();

    return onWalletConnect();
  }, [isConnected, onWalletConnect, onWalletSwitch]);

  if (!isAuthenticated) return null;

  return (
    <Button className='web3-connect' onClick={onClick} {...props}>
      {title}
    </Button>
  );
});

export default Web3WalletConnectButton;
